import { z } from 'zod';
import { mutationWithAuth, queryWithAuth } from '../graphql-helpers';
import { GET_USER, GET_USERS, ADD_USER, EDIT_USER, DELETE_USER } from '../graphql/users';

export const UserSchema = z
  .object({
    id: z.string(),
    name: z.string().optional(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    email: z.string().email(),
    phone: z.string().optional(),
    sexPreference: z.array(z.string()).optional(),
    colorPreference: z.array(z.string()).optional(),
    type: z.string().optional(),
    ranking: z.number().optional(),
    startedRanking: z.number().optional(),
    comment: z.string().optional(),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
  })
  .passthrough();

export type User = z.infer<typeof UserSchema> & {
  id: string;
};

export const UsersSchema = z.array(UserSchema);

export async function listUsers(getAccessTokenFn?: () => Promise<string | null>) {
  const response = await queryWithAuth<{ getUsers: Array<z.infer<typeof UserSchema>> }>(
    GET_USERS,
    undefined,
    getAccessTokenFn,
    true,
  );

  const users = response.getUsers;
  return users.map((user) => ({
    ...user,
    name: user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.name,
  })) as User[];
}

export async function getUser(id: string, getAccessTokenFn?: () => Promise<string | null>) {
  const response = await queryWithAuth<{ getUser: z.infer<typeof UserSchema> }>(
    GET_USER,
    { id },
    getAccessTokenFn,
    true,
  );

  const user = response.getUser;
  return {
    ...user,
    name: user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.name,
  } as User;
}

export async function createUser(
  input: Partial<User>,
  getAccessTokenFn?: () => Promise<string | null>,
) {
  const userInput = {
    firstName: input.firstName || '',
    lastName: input.lastName || '',
    email: input.email || '',
    phone: input.phone || '',
    sexPreference: input.sexPreference || [],
    colorPreference: input.colorPreference || [],
    type: input.type || '',
  };

  const response = await mutationWithAuth<{ addUser: z.infer<typeof UserSchema> }>(
    ADD_USER,
    { input: userInput },
    getAccessTokenFn,
    false,
  );

  const user = response.addUser;
  return {
    ...user,
    name: user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.name,
  } as User;
}

export async function updateUser(
  id: string,
  input: Partial<User>,
  getAccessTokenFn?: () => Promise<string | null>,
) {
  const editUserInput: Record<string, unknown> = {};
  if (input.firstName !== undefined) editUserInput.firstName = input.firstName;
  if (input.lastName !== undefined) editUserInput.lastName = input.lastName;
  if (input.email !== undefined) editUserInput.email = input.email;
  if (input.phone !== undefined) editUserInput.phone = input.phone;
  if (input.sexPreference !== undefined) editUserInput.sexPreference = input.sexPreference;
  if (input.colorPreference !== undefined) editUserInput.colorPreference = input.colorPreference;
  if (input.type !== undefined) editUserInput.type = input.type;
  if (input.ranking !== undefined) editUserInput.ranking = input.ranking;
  if (input.comment !== undefined) editUserInput.comment = input.comment;

  const response = await mutationWithAuth<{ editUser: z.infer<typeof UserSchema> }>(
    EDIT_USER,
    { id, input: editUserInput },
    getAccessTokenFn,
    true, // Requires authentication
  );

  const user = response.editUser;
  return {
    ...user,
    name: user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.name,
  } as User;
}

export async function deleteUser(id: string, getAccessTokenFn?: () => Promise<string | null>) {
  const response = await mutationWithAuth<{ deleteUser: boolean }>(
    DELETE_USER,
    { id },
    getAccessTokenFn,
    true,
  );

  return { success: response.deleteUser };
}
